/**
 * Chat overflow (⋯) — Comments, Activity, Print, Export (md / pdf / docx).
 * Shared by chat header + virtual file viewer.
 *
 * PDF/Word hit `/api/export/document` so downloads are real files — not a blank
 * `window.open(..., noopener)` tab or HTML saved as a fake `.doc`.
 */

'use client'

import { useCallback, useMemo, useState } from 'react'
import {
  IconDots,
  IconDownload,
  IconFileText,
  IconFileTypePdf,
  IconMessage,
  IconActivity,
  IconPrinter
} from '@tabler/icons-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { markdownToPrintHtml } from '@/lib/export/markdown-blocks'

export type ChatOverflowExportPayload = {
  title: string
  markdown: string
  plain: string
}

type ChatOverflowMenuProps = {
  payload: ChatOverflowExportPayload
  onComments?: () => void
  onActivity?: () => void
  className?: string
  /** Ghost icon button size — header uses size-8. */
  triggerClassName?: string
}

/** Strip known doc suffixes so `Cover_Letter.md` + `.md` doesn’t become `.md.md`. */
function stripDocExtensions(name: string) {
  return name.replace(
    /(?:\.(?:md|markdown|txt|rtf|pdf|docx?|odt|html?|csv))+$/i,
    ''
  )
}

function sanitizeFilename(raw: string) {
  const trimmed = stripDocExtensions(raw.trim() || 'chat').trim() || 'chat'
  return trimmed.replace(/[\\/:*?"<>|]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 64) || 'chat'
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.style.display = 'none'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  // Delay revoke so Safari finishes the download handoff.
  window.setTimeout(() => URL.revokeObjectURL(url), 2_000)
}

function filenameFromDisposition(header: string | null, fallback: string) {
  if (!header) return fallback
  const match = /filename\*?=(?:UTF-8''|")?([^\";]+)"?/i.exec(header)
  if (!match?.[1]) return fallback
  try {
    return decodeURIComponent(match[1].replace(/"/g, '').trim()) || fallback
  } catch {
    return match[1].replace(/"/g, '').trim() || fallback
  }
}

async function exportViaApi(
  format: 'pdf' | 'docx',
  title: string,
  content: string,
  fallbackName: string
) {
  const res = await fetch('/api/export/document', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ format, title, content })
  })
  if (!res.ok) {
    let detail = `Export failed (${res.status})`
    try {
      const data = (await res.json()) as { error?: string }
      if (data?.error) detail = data.error
    } catch {
      // ignore
    }
    throw new Error(detail)
  }
  const blob = await res.blob()
  const name = filenameFromDisposition(
    res.headers.get('Content-Disposition'),
    fallbackName
  )
  downloadBlob(blob, name)
}

/**
 * Print via a blob: iframe — writing into about:blank (or window.open+noopener)
 * leaves Safari/Chrome on about:blank with an empty print preview.
 * Renders markdown (headings/bold) — not a monospace dump of `**` / `#`.
 */
function printMarkdownDocument(title: string, markdown: string) {
  const html = markdownToPrintHtml(title, markdown)
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' })
  const blobUrl = URL.createObjectURL(blob)

  const iframe = document.createElement('iframe')
  iframe.setAttribute('aria-hidden', 'true')
  iframe.setAttribute('title', 'Print')
  iframe.style.position = 'fixed'
  iframe.style.right = '0'
  iframe.style.bottom = '0'
  iframe.style.width = '0'
  iframe.style.height = '0'
  iframe.style.border = '0'
  iframe.style.visibility = 'hidden'

  const cleanup = () => {
    window.setTimeout(() => {
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe)
      URL.revokeObjectURL(blobUrl)
    }, 1_500)
  }

  iframe.onload = () => {
    const frameWindow = iframe.contentWindow
    if (!frameWindow) {
      cleanup()
      toast.error('Couldn’t open print dialog')
      return
    }
    // Wait a frame so layout paints before the print dialog.
    window.setTimeout(() => {
      try {
        frameWindow.focus()
        frameWindow.print()
      } catch {
        toast.error('Couldn’t open print dialog')
      } finally {
        cleanup()
      }
    }, 50)
  }

  document.body.appendChild(iframe)
  iframe.src = blobUrl
}

export function ChatOverflowMenu({
  payload,
  onComments,
  onActivity,
  className,
  triggerClassName
}: ChatOverflowMenuProps) {
  const [open, setOpen] = useState(false)
  const [exporting, setExporting] = useState(false)
  const baseName = useMemo(() => sanitizeFilename(payload.title), [payload.title])

  const handleExportMarkdown = useCallback(() => {
    if (!payload.plain.trim()) {
      toast.message('Nothing to export yet')
      return
    }
    downloadBlob(
      new Blob([payload.markdown || payload.plain], { type: 'text/markdown;charset=utf-8' }),
      `${baseName}.md`
    )
  }, [baseName, payload.markdown, payload.plain])

  const runBinaryExport = useCallback(
    async (format: 'pdf' | 'docx') => {
      if (!payload.plain.trim()) {
        toast.message('Nothing to export yet')
        return
      }
      if (exporting) return
      setExporting(true)
      const toastId = toast.loading(format === 'pdf' ? 'Building PDF…' : 'Building Word doc…')
      try {
        await exportViaApi(
          format,
          payload.title,
          payload.markdown?.trim() ? payload.markdown : payload.plain,
          `${baseName}.${format === 'pdf' ? 'pdf' : 'docx'}`
        )
        toast.success(format === 'pdf' ? 'PDF downloaded' : 'Word document downloaded', {
          id: toastId
        })
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Export failed', { id: toastId })
      } finally {
        setExporting(false)
      }
    },
    [baseName, exporting, payload.markdown, payload.plain, payload.title]
  )

  const handleExportPdf = useCallback(() => {
    void runBinaryExport('pdf')
  }, [runBinaryExport])

  const handleExportDoc = useCallback(() => {
    void runBinaryExport('docx')
  }, [runBinaryExport])

  const handlePrint = useCallback(() => {
    const source = payload.markdown?.trim() || payload.plain
    if (!source.trim()) {
      toast.message('Nothing to print yet')
      return
    }
    printMarkdownDocument(payload.title, source)
  }, [payload.markdown, payload.plain, payload.title])

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={cn('size-8 shrink-0', triggerClassName, className)}
          aria-label="More"
          title="More"
          disabled={exporting}
        >
          <IconDots className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuItem
          onSelect={() => {
            onComments?.()
          }}
        >
          <IconMessage className="mr-2 size-4" />
          Comments
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => {
            onActivity?.()
          }}
        >
          <IconActivity className="mr-2 size-4" />
          Activity
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={event => {
            // Keep focus from being stolen by menu close before print hands off.
            event.preventDefault()
            window.setTimeout(() => handlePrint(), 0)
          }}
        >
          <IconPrinter className="mr-2 size-4" />
          Print
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <IconDownload className="mr-2 size-4" />
            Export
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="w-48">
            <DropdownMenuItem onSelect={handleExportMarkdown}>
              <IconFileText className="mr-2 size-4" />
              Export as Markdown
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={exporting}
              onSelect={event => {
                event.preventDefault()
                handleExportPdf()
              }}
            >
              <IconFileTypePdf className="mr-2 size-4" />
              Export as PDF
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={exporting}
              onSelect={event => {
                event.preventDefault()
                handleExportDoc()
              }}
            >
              <IconFileText className="mr-2 size-4" />
              Export as Word
            </DropdownMenuItem>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
